import { JobSiteMaterialsPanel } from "@/components/JobSiteMaterialsPanel";
import MaterialCatalogue from "@/components/MaterialCatalogue";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Search, Edit3, Trash2, AlertTriangle, History, Minus, Boxes,
  ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, X, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";

// UNITS — must match backend MaterialUnit literal
const UNITS = [
  { value: "ea",  label: "Each / qty" },
  { value: "kg",  label: "Kilograms" },
  { value: "ton", label: "Tons" },
  { value: "m3",  label: "Cubic metres (m³)" },
  { value: "m2",  label: "Square metres (m²)" },
  { value: "m",   label: "Metres (lineal)" },
  { value: "L",   label: "Litres" },
];
const UNIT_SHORT = {
  ea: "ea", kg: "kg", ton: "t", m3: "m³", m2: "m²", m: "m", L: "L",
};
const unitShort = (u) => UNIT_SHORT[u] || u;

const MOVE_BADGE = {
  take:    { label: "Out",    icon: ArrowUpFromLine,   color: "bg-orange-100 text-orange-800 border-orange-300" },
  return:  { label: "Return", icon: ArrowDownToLine,   color: "bg-green-100  text-green-800  border-green-300" },
  adjust:  { label: "Adjust", icon: ClipboardCheck,    color: "bg-blue-100   text-blue-800   border-blue-300"   },
  opening: { label: "Open",   icon: Boxes,             color: "bg-zinc-100   text-zinc-700   border-zinc-300"   },
};

export default function Materials() {
  return <MaterialCatalogue yard={<YardMaterials />} jobs={<JobMaterials />} />;
}

function JobMaterials() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job_id") || "";
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let current = true;
    api.get("/jobs").then(({data}) => { if (current) setJobs(data); })
      .catch(() => { if (current) setError("Could not load jobs. Reload to try again."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, []);
  return <div className="space-y-4">
    <label className="block text-sm font-medium">Job
      <select className="block mt-2 border rounded-md bg-background min-h-11 w-full max-w-lg px-3" value={jobId} disabled={loading} onChange={event => {
        const next = new URLSearchParams(params);
        next.set("tab", "jobs");
        if (event.target.value) next.set("job_id", event.target.value); else next.delete("job_id");
        setParams(next);
      }}>
        <option value="">{loading ? "Loading jobs…" : "Choose a job"}</option>
        {jobId && !jobs.some(job => job.id === jobId) && <option value={jobId}>Selected job</option>}
        {jobs.map(job => <option key={job.id} value={job.id}>{job.title}</option>)}
      </select>
    </label>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {jobId ? <><Link className="inline-flex min-h-11 items-center text-sm text-[#FF5F15]" to={`/app/jobs/${encodeURIComponent(jobId)}/materials`}>Open job card →</Link><JobSiteMaterialsPanel key={jobId} jobId={jobId} showWorkspaceLink={false} /></> : <p className="text-sm text-muted-foreground">Choose a job to record materials, take yard stock, or review deliveries. These are the same records shown on the job card.</p>}
  </div>;
}

function YardMaterials() {
  const [params] = useSearchParams();
  const sourceJobId = params.get("job_id") || "";
  const { user } = useAuth();
  const canEditCatalogue = ["admin", "office", "super_admin", "quantity_surveyor"].includes(user.role);
  const canDelete = ["admin", "office", "super_admin"].includes(user.role);
  const canAdjust = ["admin", "office", "super_admin"].includes(user.role);

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);  // {mat, mode: 'take'|'return'|'adjust'}
  const [historyTarget, setHistoryTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/materials", { params: search ? { q: search } : {} });
    setItems(data.filter(m => m.yard_stocked !== false && m.active !== false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totals = useMemo(() => {
    const low = items.filter((m) => m.low_stock_threshold != null && m.quantity <= m.low_stock_threshold);
    const empty = items.filter((m) => m.quantity <= 0);
    return { count: items.length, low: low.length, empty: empty.length };
  }, [items]);

  const remove = async (m) => {
    if (!window.confirm(`Deactivate ${m.name}? Stock and movement history will be retained.`)) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Material deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="materials-page" className="space-y-4">
      <PageHeader
        tag="/ Yard inventory"
        title="Yard stock"
        subtitle="Live stock in the yard. Take what you need, return what's left — keep the office in the loop."
        action={canEditCatalogue &&
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditing(null)}
                      className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                      data-testid="new-material-button">
                <Plus className="w-4 h-4 mr-2" /> Add material
              </Button>
            </DialogTrigger>
            <MaterialForm
              key={editing?.id || "new"}
              initial={editing}
              onDone={() => { setOpen(false); setEditing(null); load(); }}
            />
          </Dialog>
        }
      />

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="materials-summary">
        <SummaryTile label="Items" value={totals.count} accent="bg-[#09090B]" testid="mat-tile-count" />
        <SummaryTile label="Low stock" value={totals.low} accent="bg-yellow-500" testid="mat-tile-low" alert={totals.low > 0} />
        <SummaryTile label="Out of stock" value={totals.empty} accent="bg-red-600" testid="mat-tile-empty" alert={totals.empty > 0} />
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-5">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or category…"
          className="pl-9 rounded-sm border-zinc-400 h-9"
          data-testid="materials-search-input"
        />
        {search && (
          <button onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                  aria-label="Clear search"
                  data-testid="materials-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={search ? "No matching materials" : "No materials in the yard yet"}
          description={search ? "Try a different search." : "Add your first material to start tracking the yard."}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <MaterialCard
              key={m.id}
              m={m}
              canEdit={canEditCatalogue}
              canDelete={canDelete}
              canAdjust={canAdjust}
              onTake={() => setActionTarget({ mat: m, mode: "take" })}
              onReturn={() => setActionTarget({ mat: m, mode: "return" })}
              onAdjust={() => setActionTarget({ mat: m, mode: "adjust" })}
              onEdit={() => { setEditing(m); setOpen(true); }}
              onDelete={() => remove(m)}
              onHistory={() => setHistoryTarget(m)}
            />
          ))}
        </div>
      )}

      {/* Action dialog (take / return / adjust) */}
      {actionTarget && (
        <MovementDialog
          initialJobId={sourceJobId}
          mat={actionTarget.mat}
          mode={actionTarget.mode}
          onClose={() => setActionTarget(null)}
          onDone={() => { setActionTarget(null); load(); }}
        />
      )}

      {/* Movement history dialog */}
      {historyTarget && (
        <HistoryDialog
          mat={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------
function SummaryTile({ label, value, accent, alert, testid }) {
  return (
    <div className={`hard-card p-3 ${alert ? "border-2 border-current" : ""}`} data-testid={testid}>
      <div className={`${accent} text-white inline-block text-[9px] font-bold uppercase tracking-widest px-2 py-0.5`}>{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 leading-none">{value}</div>
    </div>
  );
}

// --------------------------------------------------------
function MaterialCard({ m, canEdit, canDelete, canAdjust, onTake, onReturn, onAdjust, onEdit, onDelete, onHistory }) {
  const isLow = m.low_stock_threshold != null && m.quantity <= m.low_stock_threshold;
  const isOut = m.quantity <= 0;
  const borderClass = isOut
    ? "border-2 border-red-500"
    : isLow ? "border-2 border-yellow-500" : "border border-zinc-300";

  return (
    <div className={`hard-card ${borderClass}`} data-testid={`material-card-${m.id}`}>
      {/* Photo or icon banner */}
      <div className="h-20 bg-zinc-100 border-b border-zinc-200 overflow-hidden flex items-center justify-center">
        {m.photo_path ? (
          <AuthImage path={m.photo_path} alt={m.name} className="w-full h-full object-cover" />
        ) : (
          <Boxes className="w-10 h-10 text-zinc-300" />
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-bold uppercase tracking-tight text-base leading-tight truncate"
                data-testid={`material-name-${m.id}`}>{m.name}</h3>
            {m.category && (
              <div className="mt-1 inline-block text-[9px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5">
                {m.category}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-end gap-1 justify-end">
              <span className={`font-display font-black text-3xl leading-none ${isOut ? "text-red-600" : isLow ? "text-yellow-700" : ""}`}
                    data-testid={`material-qty-${m.id}`}>
                {m.quantity.toLocaleString("en-NZ", { maximumFractionDigits: 3 })}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">
                {unitShort(m.unit)}
              </span>
            </div>
            {m.cost_per_unit != null && m.cost_per_unit > 0 && (
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                ${m.cost_per_unit.toFixed(2)} / {unitShort(m.unit)}
              </div>
            )}
          </div>
        </div>

        {(isLow || isOut) && (
          <div className={`mt-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${isOut ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-800"}`}
               data-testid={`material-alert-${m.id}`}>
            <AlertTriangle className="w-3 h-3" />
            {isOut ? "Out of stock" : `Low — alert at ${m.low_stock_threshold} ${unitShort(m.unit)}`}
          </div>
        )}

        {m.notes && (
          <div className="mt-3 text-xs text-zinc-600 line-clamp-2">{m.notes}</div>
        )}

        {/* Action buttons */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            onClick={onTake}
            disabled={isOut}
            className="h-9 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs disabled:opacity-50"
            data-testid={`material-take-${m.id}`}>
            <Minus className="w-3.5 h-3.5 mr-1" /> Take
          </Button>
          <Button
            onClick={onReturn}
            variant="outline"
            className="h-9 rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs"
            data-testid={`material-return-${m.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Return
          </Button>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <Button size="sm" variant="ghost"
                  onClick={onHistory}
                  className="h-7 rounded-sm text-[10px] uppercase tracking-widest font-bold text-zinc-600 hover:text-[#FF5F15]"
                  data-testid={`material-history-${m.id}`}>
            <History className="w-3.5 h-3.5 mr-1" /> History
          </Button>
          <div className="flex items-center gap-1">
            {canAdjust && (
              <Button size="sm" variant="ghost"
                      onClick={onAdjust}
                      className="h-7 rounded-sm text-[10px] uppercase tracking-widest font-bold text-blue-700"
                      data-testid={`material-adjust-${m.id}`}>
                <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Stock-take
              </Button>
            )}
            <Button disabled={!canEdit} size="icon" variant="ghost"
                    onClick={onEdit}
                    className="h-7 w-7 rounded-sm text-zinc-500 hover:text-zinc-900"
                    data-testid={`material-edit-${m.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
            {canDelete && (
              <Button size="icon" variant="ghost"
                      onClick={onDelete}
                      className="h-7 w-7 rounded-sm text-zinc-500 hover:text-red-600"
                      data-testid={`material-delete-${m.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------
function MaterialForm({ initial, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "",
    unit: initial?.unit || "ton",
    quantity: initial ? "" : 0,  // only used at creation; edits cannot mutate quantity
    low_stock_threshold: initial?.low_stock_threshold ?? "",
    cost_per_unit: initial?.cost_per_unit ?? "",
    photo_path: initial?.photo_path || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/materials/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "material");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_path: data.storage_path }));
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const num = (v) => v === "" || v == null ? null : Number(v);
      const payload = {
        name: form.name.trim(),
        category: (form.category || "").trim().toLowerCase() || null,
        unit: form.unit,
        low_stock_threshold: num(form.low_stock_threshold),
        cost_per_unit: num(form.cost_per_unit),
        photo_path: form.photo_path || null,
        notes: form.notes || null,
      };
      if (!isEdit) {
        payload.quantity = num(form.quantity) ?? 0;
        await api.post("/materials", payload);
        toast.success("Material added");
      } else {
        await api.patch(`/materials/${initial.id}`, payload);
        toast.success("Material updated");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">
          {isEdit ? "Edit material" : "Add material"}
        </DialogTitle>
        <DialogDescription>
          {isEdit ? "Update the catalogue details. Use Take / Return / Stock-take to change quantities." : "Tell the crew what's in the yard and how it's counted."}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Name</Label>
          <Input required value={form.name} onChange={set("name")}
                 className="mt-2 rounded-sm border-zinc-400"
                 placeholder="e.g. GAP65 aggregate"
                 data-testid="material-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Category</Label>
            <Input list="material-categories" value={form.category} onChange={set("category")}
                   className="mt-2 rounded-sm border-zinc-400"
                   placeholder="aggregate / concrete / pipe…"
                   data-testid="material-category-input" />
            <datalist id="material-categories">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <Label className="label-tag">Unit</Label>
            <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })} disabled={isEdit}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="material-unit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isEdit && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                Unit locked after creation
              </div>
            )}
          </div>
        </div>

        {!isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Opening stock</Label>
              <div className="mt-2 relative">
                <Input type="number" step="0.001" min="0" value={form.quantity}
                       onChange={set("quantity")}
                       className="rounded-sm border-zinc-400 pr-10"
                       placeholder="0"
                       data-testid="material-opening-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 pointer-events-none">
                  {unitShort(form.unit)}
                </span>
              </div>
            </div>
            <div>
              <Label className="label-tag">Low-stock alert</Label>
              <div className="mt-2 relative">
                <Input type="number" step="0.001" min="0" value={form.low_stock_threshold}
                       onChange={set("low_stock_threshold")}
                       className="rounded-sm border-zinc-400 pr-10"
                       placeholder="optional"
                       data-testid="material-threshold-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 pointer-events-none">
                  {unitShort(form.unit)}
                </span>
              </div>
            </div>
          </div>
        )}

        {isEdit && (
          <div>
            <Label className="label-tag">Low-stock alert</Label>
            <div className="mt-2 relative max-w-[200px]">
              <Input type="number" step="0.001" min="0" value={form.low_stock_threshold}
                     onChange={set("low_stock_threshold")}
                     className="rounded-sm border-zinc-400 pr-10"
                     placeholder="optional"
                     data-testid="material-threshold-input" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 pointer-events-none">
                {unitShort(form.unit)}
              </span>
            </div>
          </div>
        )}

        <div>
          <Label className="label-tag">Cost per {unitShort(form.unit)} <span className="text-zinc-400 normal-case font-normal">— NZD, optional · adds to Job Cost</span></Label>
          <div className="mt-2 relative max-w-[200px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
            <Input type="number" step="0.01" min="0" value={form.cost_per_unit}
                   onChange={set("cost_per_unit")}
                   className="rounded-sm border-zinc-400 pl-7"
                   placeholder="0.00"
                   data-testid="material-cost-input" />
          </div>
        </div>

        <div>
          <Label className="label-tag">Photo <span className="text-zinc-400 normal-case">— optional</span></Label>
          {form.photo_path ? (
            <div className="mt-2 relative inline-block border border-zinc-300 bg-zinc-50">
              <AuthImage path={form.photo_path} alt="material" className="h-28 w-auto object-cover" />
              <button type="button"
                      onClick={() => setForm((f) => ({ ...f, photo_path: "" }))}
                      className="absolute top-1 right-1 bg-zinc-900/80 text-white rounded-full p-1 hover:bg-red-600"
                      data-testid="material-photo-remove">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex items-center justify-center gap-2 h-16 border-2 border-dashed border-zinc-300 hover:border-[#FF5F15] cursor-pointer text-sm text-zinc-500 hover:text-[#FF5F15] transition-colors"
                   data-testid="material-photo-upload">
              {photoBusy ? <span className="font-mono">Uploading…</span> : <><Camera className="w-4 h-4" /> Tap to add photo</>}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} disabled={photoBusy} />
            </label>
          )}
        </div>

        <div>
          <Label className="label-tag">Notes</Label>
          <Textarea rows={2} value={form.notes} onChange={set("notes")}
                    className="mt-2 rounded-sm border-zinc-400"
                    data-testid="material-notes-input" />
        </div>

        <DialogFooter>
          <Button type="submit" disabled={busy}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold"
                  data-testid="material-submit-button">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add to yard"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// --------------------------------------------------------
function MovementDialog({ mat, mode, initialJobId = "", onClose, onDone }) {
  const isTake = mode === "take";
  const isReturn = mode === "return";
  const isAdjust = mode === "adjust";

  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(initialJobId);
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isAdjust) return;
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, [isAdjust]);

  const submit = async (e) => {
    e.preventDefault();
    const q = Number(quantity);
    if (!Number.isFinite(q) || q < 0) {
      toast.error("Enter a valid quantity");
      return;
    }
    if (isTake) {
      if (q <= 0) return toast.error("Quantity must be greater than zero");
      if (!jobId) return toast.error("Pick a job");
      if (q > mat.quantity) return toast.error(`Only ${mat.quantity} ${unitShort(mat.unit)} available`);
    }
    setBusy(true);
    try {
      if (isTake) {
        await api.post(`/materials/${mat.id}/take`, { quantity: q, job_id: jobId, notes: notes || null });
        toast.success(`Took ${q} ${unitShort(mat.unit)} of ${mat.name}`);
      } else if (isReturn) {
        await api.post(`/materials/${mat.id}/return`, { quantity: q, job_id: jobId || null, notes: notes || null });
        toast.success(`Returned ${q} ${unitShort(mat.unit)} to the yard`);
      } else {
        await api.post(`/materials/${mat.id}/adjust`, { new_quantity: q, notes: notes || null });
        toast.success("Stock-take recorded");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const title = isTake ? "Take material" : isReturn ? "Return to yard" : "Stock-take adjustment";
  const subtitle = isTake
    ? `On hand: ${mat.quantity} ${unitShort(mat.unit)}`
    : isReturn
      ? `Currently in yard: ${mat.quantity} ${unitShort(mat.unit)}`
      : `Current: ${mat.quantity} ${unitShort(mat.unit)} → enter the actual yard count`;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid={`material-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{title}</DialogTitle>
          <DialogDescription>{mat.name} · {subtitle}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-tag">
              {isAdjust ? "New on-hand quantity" : "Quantity"}
            </Label>
            <div className="mt-2 relative">
              <Input type="number" step="0.001" min="0"
                     value={quantity} onChange={(e) => setQuantity(e.target.value)}
                     className="rounded-sm border-zinc-400 pr-12 text-lg font-mono"
                     placeholder="0"
                     required
                     autoFocus
                     data-testid={`material-${mode}-qty-input`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-widest text-zinc-500">
                {unitShort(mat.unit)}
              </span>
            </div>
            {isTake && Number(quantity) > 0 && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid={`material-take-preview`}>
                Yard will drop to {(mat.quantity - Number(quantity)).toFixed(3)} {unitShort(mat.unit)}
              </div>
            )}
          </div>

          {!isAdjust && (
            <div>
              <Label className="label-tag">Job {isTake ? "" : <span className="text-zinc-400 normal-case font-normal">(optional)</span>}</Label>
              <Select value={jobId} onValueChange={setJobId}>
                <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid={`material-${mode}-job-select`}>
                  <SelectValue placeholder={isTake ? "Pick a job" : "No job"} />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="label-tag">Notes <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-2 rounded-sm border-zinc-400"
                      placeholder={isReturn ? "e.g. Unused topsoil, dry" : ""}
                      data-testid={`material-${mode}-notes-input`} />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy}
                    className={`rounded-sm uppercase tracking-wider font-bold ${
                      isTake ? "bg-[#FF5F15] hover:bg-[#E65100]" :
                      isReturn ? "bg-green-600 hover:bg-green-700" :
                                 "bg-blue-600 hover:bg-blue-700"
                    } text-white`}
                    data-testid={`material-${mode}-submit`}>
              {busy ? "Saving…" :
                isTake ? "Take it" :
                isReturn ? "Return to yard" :
                           "Save stock-take"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------
function HistoryDialog({ mat, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/materials/${mat.id}/movements`)
      .then((r) => { if (!cancelled) setRows(r.data); })
      .catch(() => { if (!cancelled) toast.error("Failed to load history"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mat.id]);

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-xl max-h-[80vh] overflow-y-auto" data-testid="material-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Movement history</DialogTitle>
          <DialogDescription>{mat.name} · current: {mat.quantity} {unitShort(mat.unit)}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No movements yet.</div>
        ) : (
          <div className="divide-y divide-zinc-200" data-testid="material-history-list">
            {rows.map((r) => {
              const badge = MOVE_BADGE[r.type] || MOVE_BADGE.adjust;
              const Icon = badge.icon;
              const sign = r.type === "take" ? "−" : r.type === "return" || r.type === "opening" ? "+" : (r.quantity >= 0 ? "+" : "−");
              return (
                <div key={r.id} className="py-2.5 flex items-start gap-3" data-testid={`material-history-row-${r.id}`}>
                  <div className={`inline-flex items-center gap-1 border text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 ${badge.color}`}>
                    <Icon className="w-3 h-3" /> {badge.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono">
                      <span className="font-bold">{sign}{Math.abs(r.quantity).toLocaleString("en-NZ", { maximumFractionDigits: 3 })} {unitShort(r.unit)}</span>
                      <span className="text-zinc-500"> · balance {r.balance_after} {unitShort(r.unit)}</span>
                    </div>
                    <div className="text-xs text-zinc-600 mt-0.5">
                      {r.user_name || "—"}
                      {r.job_title && <> · <span className="text-zinc-900 font-bold">{r.job_title}</span></>}
                      {r.notes && <> · {r.notes}</>}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">
                      {new Date(r.created_at).toLocaleString("en-NZ")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
